import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateNoticeAmount } from '@/hooks/react-query/useDrawdowns';
import type { BatchInvestorNotice } from '@/types/drawdown';
import { formatCurrency } from '@/utils/formatters';

interface EditNoticeAmountDialogProps {
  onClose: () => void;
  fundId: string;
  drawdownId: string;
  notice: BatchInvestorNotice;
}

export function EditNoticeAmountDialog({
  onClose,
  fundId,
  drawdownId,
  notice,
}: EditNoticeAmountDialogProps) {
  const [amount, setAmount] = useState(String(notice.drawdown_amount));
  const updateAmount = useUpdateNoticeAmount();
  const parsed = Number(amount);
  const valid = amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0;

  const handleSave = () => {
    updateAmount.mutate(
      {
        fundId,
        drawdownId,
        drawdownInvestorId: notice.id,
        drawdownAmount: parsed,
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit amount - {notice.investor_name}</DialogTitle>
          <DialogDescription>
            Received so far:{' '}
            {formatCurrency(notice.amount_due - notice.net_amount_due)}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <Label htmlFor="notice-amount">Amount called</Label>
          <Input
            id="notice-amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!valid || updateAmount.isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
